import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useAuth } from 'hooks';
import OrganizationMarketplacesFilter from 'components/Bookings2/List/OrganizationMarketplacesFilter';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { useInvoiceDetailView } from './InvoiceNumberLink';

export interface DashboardProps {
    onGotoTransactions: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'block',
            justifyContent: 'center',
            background: 'white',
            height: '99%',
            width: '100%'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '70px',
            padding: '0px 10px'
        },
        title: {
            fontSize: 24,
            fontWeight: 600,
            paddingTop: 5
        },
        iframe: {
            width: '100%',
            border: 'none',
            height: 'calc(100% - 70px)'
        }
    })
);

export const Dashboard: FunctionComponent<DashboardProps> = props => {
    const classes = useStyles();

    const auth = useAuth();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const { openInvoiceDetail, invoiceDetailView } = useInvoiceDetailView();
    const openInvoiceDetailRef = useRef(openInvoiceDetail);
    const onGotoTransactionsRef = useRef(props.onGotoTransactions);
    openInvoiceDetailRef.current = openInvoiceDetail;
    onGotoTransactionsRef.current = props.onGotoTransactions;

    const [selectedMarketplaceId, setSelectedMarketplaceId] = useState(auth.marketplaceId ?? 0);

    const url = `${process.env.REACT_APP_HOST_URL}${process.env.REACT_APP_WEB_BASE_URL}payments_dashboard?access_token=${auth.token}&marketplace_id=${auth.marketplaceId}&marketplace_ids=${selectedMarketplaceId}`;

    useEffect(() => {
        const messageHandler = (event: MessageEvent) => {
            const data = event.data;
            const action: string | undefined = data?.action;

            if (action === 'goto_transactions') {
                onGotoTransactionsRef.current?.();
                return;
            }

            if (action === 'goto_invoice' || action === 'open_invoice') {
                const invoiceId = Number(data.invoice_id ?? data.invoiceId);
                const marketplaceId = Number(data.marketplace_id ?? data.marketplaceId ?? auth.marketplaceId);

                if (Number.isFinite(invoiceId) && invoiceId > 0 && Number.isFinite(marketplaceId)) {
                    openInvoiceDetailRef.current(invoiceId, marketplaceId);
                }
            }
        };

        window.addEventListener('message', messageHandler);
        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [auth.marketplaceId]);

    const header = (
        <div className={classes.header}>
            <span className={classes.title}>Dashboard</span>
            {organizationMarketplaces.length > 1 && (
                <OrganizationMarketplacesFilter
                    singleSelection={true}
                    selected={[selectedMarketplaceId]}
                    onChangeSelection={([marketplaceId]) => setSelectedMarketplaceId(marketplaceId)}
                />
            )}
        </div>
    );

    return (
        <Box className={classes.container}>
            {header}
            <iframe
                title="Payments Dashboard"
                className={classes.iframe}
                src={url}
            ></iframe>
            {invoiceDetailView}
        </Box>
    );
};

export default Dashboard;
